/**
 * Country gating registry — single source of truth for "this thing only
 * exists in these countries." Three kinds of entry:
 *
 *   - `page:<slug>`       — whole route SSG'd only in listed countries.
 *                           Sitemap, hreflang, locale picker, search index
 *                           all filter on this.
 *   - `section:<page>.<slot>` — UI block inside a shared page.
 *   - `feature:<name>`     — behaviour gate that has no visible section
 *                           (provider mount, side-effect hook).
 *
 * Why a registry instead of inline `country === "xx"` checks:
 *   - Greppable: `lib/country-gates.ts` is the audit of what varies.
 *   - Lintable: gate IDs in code must match a key here.
 *   - Discoverable: agents and humans have one place to look + one skill
 *     to read (`.claude/skills/country-gating/SKILL.md`).
 *
 * NOT here: PostHog flags (per-user A/B — `lib/experiments.ts`),
 * release-time kill switches (`lib/release-flags.ts`), or whole-country
 * enablement (`lib/build-time-flags.ts`). Those are orthogonal.
 *
 * Adding a new gate: see `.claude/skills/country-gating/SKILL.md`.
 */

import type { Country } from "@/i18n/config"
import { canonicalSlugPath } from "@/lib/localized-route-slugs"

type CountryGate = {
  /** Country codes where this thing renders / exists. Non-empty. */
  countries: readonly Country[]
  /** One-line note: what this gates and why. Read by humans + agents. */
  description: string
  /**
   * i18n key prefixes whose copy is *exclusive* to this gated surface. The
   * translation-review queue uses these to drop strings a reviewer's country can
   * never see in context (`isKeyCountryGatedOut`). Page gates are handled
   * separately, by render path — only set this for `section:`/`feature:` gates,
   * and only when the prefix is owned solely by gated content (nothing un-gated
   * renders it). A wrong entry hides real review work, so err towards omitting.
   */
  reviewKeyPrefixes?: readonly string[]
}

export const COUNTRY_GATES = {
  // --- Pages: route only SSG'd in listed countries ---
  "page:insurance": {
    countries: ["ch"],
    description:
      "Insurance coverage checker — Swiss-reimbursement market only.",
  },
  "page:membership": {
    countries: ["ch", "de"],
    description:
      "Ahead membership page. CH sells it, DE advertises it as coming-soon " +
      "(the mega-menu card links here). Waitlist NL doesn't offer membership " +
      "at all (2026-07-29 decision, AHD-9361) — no page, no promise. AT was " +
      "dropped 2026-08-28 when the Austrian launch set was trimmed to a " +
      "13-page waitlist site; it also left feature:membership-first-positioning " +
      "in the same pass, so nothing on AT points here any more.",
  },
  "page:insurance-checker-terms": {
    countries: ["ch"],
    description: "T&Cs for the CH-only insurance checker.",
  },
  "page:full-body-mri-zurich": {
    countries: ["ch"],
    description: "Zurich-specific MRI landing page.",
  },
  "page:go/bloodtest-munich": {
    countries: ["de"],
    description:
      "Munich blood-test ad landing page — DE-only (single German clinic, " +
      "Labor Becker Marienplatz). Registered so switching country from the " +
      "picker falls back to the target country's home instead of a 404. The " +
      "page also self-gates with a client-side notFound() for direct hits to " +
      "another country (single-clinic ad LP, geography not market status).",
  },
  "page:go/bloodtest-berlin": {
    countries: ["de"],
    description:
      "Berlin blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Berlin yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-frankfurt": {
    countries: ["de"],
    description:
      "Frankfurt blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Frankfurt yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-hamburg": {
    countries: ["de"],
    description:
      "Hamburg blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Hamburg yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-zurich": {
    countries: ["ch"],
    description:
      "Zurich blood-test ad landing page — CH-only (single Swiss clinic). " +
      "Registered so switching country from the picker falls back to the " +
      "target country's home instead of a 404. The page also self-gates with " +
      "a client-side notFound() for direct hits to another country " +
      "(single-clinic ad LP, geography not market status).",
  },
  "page:gift": {
    countries: ["ch"],
    description: "Gift card shop — CH / CHF only for v1.",
  },
  "page:quiz": {
    countries: ["ch", "nl"],
    description:
      "Quiz recommendation funnel (embedded + focused). CH + NL. The funnel is market-aware (lib/quiz/market.ts): it resolves what each market can actually sell via resolveServiceAvailability, so on NL the MRI question routes to the waitlist (coming-soon at checkup status), the imaging follow-up step drops (those four services are not_available), and the insurer step + coverage band + pro-forma don't render (no Dutch insurance story). DE/AT still excluded — they need the de-DE/de-AT quiz namespace first.",
    // Deliberately on a `page:` gate, against the general guidance above. The
    // `quiz.` namespace is owned *solely* by this funnel — which is the
    // condition that actually matters — and this gate's country list is the
    // only accurate answer to "can a reviewer in country X see these strings".
    // It used to live on section:marketing.quiz-cta-banner (CH-only); leaving it
    // there would hide the Dutch strings from the NL reviewer, and dropping it
    // entirely would push 182 phantom strings into the DE/AT queues for a
    // funnel those markets don't serve.
    reviewKeyPrefixes: ["quiz."],
  },
  "page:careers/prevention-specialist-gp-landing": {
    countries: ["ch"],
    description:
      "Recruitment LP for French-speaking GPs in Suisse romande — CH-only (Swiss clinical role).",
  },
  "page:free-call": {
    countries: ["ch", "de"],
    description:
      "Discovery-call booking page. Hidden on NL during the waitlist phase (AHD-6839).",
  },
  "page:insights": {
    countries: ["ch", "nl"],
    description:
      "Blog/insights listing and detail pages. NL shows the reviewed subset only — per-post hiding via hideInCountries in the CMS (AHD-6892). DE temporarily removed pending DE-market-appropriate translated content (Swiss-focused articles were leaking to /de/de/ via SEO).",
  },
  "page:compare-health-check-ups-switzerland": {
    countries: ["ch"],
    description:
      "Compare preventive health check-ups page — CH-only (Swiss positioning; NL hidden per AHD-6919).",
  },
  "page:tests/cortisol-blood-test": {
    countries: ["ch", "at"],
    description:
      "Cortisol single-biomarker add-on landing page. Opened to AT 2026-08-31 " +
      "(the Austrian launch set keeps it). The two original CH-only reasons " +
      "(AHD-3795) don't hold for AT any more: the copy is fully translated in " +
      "the de-AT `go` namespace, and the price line is hidden on markets with " +
      "no authoritative EUR figure rather than showing a CHF one. Still " +
      "CH+AT only — DE and NL haven't asked for it. Caveat: `metadata` is a " +
      "hardcoded English literal, so /at/de ships an English title until it " +
      "moves to translated keys.",
  },
  "page:tests/psa-blood-test": {
    countries: ["ch"],
    description:
      "PSA single-biomarker add-on landing page — CH-only. It's an add-on to " +
      "the Swiss Advanced blood test; EUR markets (DE/NL/AT) have no " +
      "authoritative price (Stripe is CHF-only) and the copy is CH-first " +
      "(AHD-3795).",
  },

  "page:pricing": {
    countries: ["ch", "nl", "de"],
    description:
      "Pricing page. Excluded from AT: there is no EUR price map for Austria, " +
      "so the page renders price-free, and it was already dropped from the AT " +
      "nav. Part of the 2026-08-28 trim of the Austrian launch set.",
  },
  "page:careers": {
    countries: ["ch", "nl", "de"],
    description:
      "Careers listing + job detail pages. Excluded from AT: no Austrian roles " +
      "to list while the market is pre-launch (2026-08-28 launch-set trim). " +
      "DE keeps the route but hides it from the footer.",
  },
  "page:authors": {
    countries: ["ch", "nl", "de"],
    description:
      "Author/reviewer bio pages. Excluded from AT (2026-08-28 launch-set " +
      "trim) — the only surface that links them is /insights/[slug], which AT " +
      "doesn't have either.",
  },
  "page:ambassadors": {
    countries: ["ch", "nl", "de"],
    description:
      "Ambassador programme landing page + its /applied thank-you page. " +
      "Excluded from AT (2026-08-28 launch-set trim); DE keeps the route but " +
      "hides it from the footer.",
  },

  "page:faq": {
    countries: ["ch", "nl", "de"],
    description:
      "FAQ listing (plus its /[slug] and /category/[slug] children, which " +
      "404 on their own once the CMS returns nothing). Excluded from AT in " +
      "the 2026-08-28 launch-set trim — answering Swiss questions in an " +
      "Austrian market is worse than not answering yet. DE keeps the route " +
      "but noindexes it (NOINDEX_IN_COUNTRIES).",
  },
  "page:biomarkers": {
    countries: ["ch", "nl", "de"],
    description:
      "Biomarker glossary listing (/biomarkers, served from v/[code]) and its " +
      "detail pages. Excluded from AT in the 2026-08-28 launch-set trim: a " +
      "145-marker catalogue is the single largest de-AT authoring debt and " +
      "nothing in the Austrian set links it.",
  },
  "page:tests/lpa": {
    countries: ["ch", "nl", "de", "at"],
    description:
      "Lp(a) single-biomarker landing page. AT was briefly excluded in the " +
      "2026-08-28 launch-set trim and put back the same day: the body copy is " +
      "fully translated in the de-AT `go` namespace, and the hero hides the " +
      "price line on markets with no authoritative EUR figure rather than " +
      "mislabelling a CHF one. Caveat: `metadata` here is still a hardcoded " +
      "English literal, so /at/de (like /de/de and /nl/nl already) ships an " +
      "English title until it moves to translated keys.",
  },
  "page:symptoms": {
    countries: ["ch", "nl", "de"],
    description:
      "Symptoms lens listing + detail. Already behind SYMPTOMS_ENABLED; the " +
      "country gate is additive so AT stays dark even if the flag goes on " +
      "(2026-08-28 launch-set trim).",
  },
  "page:teams": {
    countries: ["ch", "nl", "de"],
    description:
      "B2B teams page. Already behind the teams-page-enabled release flag; " +
      "the country gate is additive. Excluded from AT (2026-08-28 launch-set " +
      "trim) — the page renders hardcoded English strings rather than " +
      "next-intl, so it would show English on /at/de.",
  },
  "page:tools": {
    countries: ["ch", "de", "nl"],
    description:
      "The /tools index, listing the free health calculators. CH + DE + NL. " +
      "Its own closing CTA is a check-up, which all three markets sell or " +
      "waitlist, so the membership reasoning that kept the calculators " +
      "CH-only never applied to the index itself, and NL's lack of a " +
      "membership page does not either: this page never links to one. Ships " +
      "in all three Swiss locales, at /de/de/tools alone since DE offers " +
      "German only, and in both NL locales. Which locales actually have " +
      "something to list is derived from HEALTH_TOOLS (lib/health-tools.ts), " +
      "so the index cannot outlive its content in any market: on NL it is " +
      "empty of cards until at least one calculator's own gate opens there, " +
      "which is why this change is meant to land after them.",
  },
  "page:tools/bmi-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "BMI calculator SEO tool page. CH + DE + NL. The route's own LOCALES " +
      "holds de/en/fr/nl (the registry has no locale-level gate), so CH " +
      "prerenders de/en/fr, DE prerenders `de`, its only language, and NL " +
      "prerenders nl + en, the two it offers. DE joined once the German copy " +
      "landed in messages/de-DE.json; its membership panel keeps the copy, " +
      "and isBookingOpen(country, 'membership') already turns that CTA into " +
      "the DE waitlist, the same coming-soon promise /de/de/membership " +
      "makes. NL needed one thing besides the Dutch and NL-English copy, and " +
      "it is that same panel: this market sells no membership at all " +
      "(page:membership, AHD-9361, 'no page, no promise'), so the panel's " +
      "own /membership link would hard-404 there. It now reads " +
      "page:membership rather than a country literal, and where that gate is " +
      "closed the panel points at the advanced blood test instead, which is " +
      "the product this market does sell. AT still out: trimmed to a " +
      "13-page waitlist site.",
  },
  "page:tools/bmr-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "BMR (basal metabolic rate) calculator SEO tool page. CH + DE + NL, on " +
      "the same terms as the BMI one: LOCALES holds de/en/fr/nl, so CH " +
      "prerenders de/en/fr, DE prerenders `de` alone, reading the German " +
      "copy in messages/de-DE.json, and NL prerenders nl + en, reading " +
      "messages/nl.json and messages/en-NL.json. Its membership panel " +
      "resolves to the DE waitlist in Germany; in NL there is no membership " +
      "to resolve to at all (page:membership, AHD-9361), so the panel reads " +
      "that gate and points at the advanced blood test instead. Mifflin-St " +
      "Jeor and Harris-Benedict carry no country term, so the rest of the " +
      "Dutch rollout was the copy. AT still out: trimmed to a 13-page " +
      "waitlist site.",
  },
  "page:tools/score2-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "SCORE2 cardiovascular-risk calculator SEO tool page. CH + DE, and it " +
      "took more than a translation to get there, unlike every other " +
      "calculator: SCORE2 is recalibrated per CVD risk region, Switzerland " +
      "is a low-risk region country and Germany a moderate one, so the gate " +
      "is paired with SCORE2_REGION_BY_COUNTRY (lib/score2.ts), which is " +
      "deliberately partial: a market with no entry there cannot be " +
      "computed at all. Germany's `moderate` entry landed in the same change " +
      "as this country list, and the difference is not cosmetic — the worked " +
      "example both pages print scores 6.1% on the Swiss calibration and " +
      "7.8% on the German one. The page also asks its two lipids in mg/dL " +
      "there, the unit German labs report " +
      "(feature:tools-mgdl-lipids, lib/lipid-units.ts). LOCALES holds " +
      "de/en/fr/nl, so CH prerenders de/en/fr, DE prerenders `de`, its only " +
      "language, and NL prerenders nl + en. The Netherlands needed the same " +
      "region entry Germany did, and it is the other answer: NL is LOW, like " +
      "Switzerland, so /nl/* scores the worked example at the same 6.1% the " +
      "Swiss pages print rather than Germany's 7.8%, and its copy names the " +
      "Netherlands rather than repeating Switzerland's claim. It did NOT " +
      "need the mg/dL entry: Dutch laboratories report lipids in mmol/L, so " +
      "feature:tools-mgdl-lipids stays DE-only. Its closing panel reads " +
      "page:membership, which NL does not have (AHD-9361), and points at the " +
      "advanced blood test instead. AT stays out: it is moderate like " +
      "Germany, but it is trimmed to a 13-page waitlist site, so its region " +
      "entry belongs in the change that gives it the page.",
  },
  "page:tools/fib4-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "FIB-4 liver fibrosis calculator SEO tool page. CH + DE, on the same " +
      "terms as the BMI and BMR ones rather than the SCORE2 one: FIB-4 is " +
      "four routine blood values and a square root, with no fitted " +
      "coefficients and no regional recalibration (lib/fib4.ts), so there " +
      "was nothing in the model a second market needed wired up and the " +
      "German copy in messages/de-DE.json was the whole of the rollout. " +
      "LOCALES holds de/en/fr, so CH prerenders all three and DE prerenders " +
      "`de`, its only language. The German copy is not the Swiss one with " +
      "the orthography changed: German labs name the two enzymes AST and " +
      "ALT (GOT and GPT on older reports), where Swiss ones write ASAT and " +
      "ALAT, and they count platelets in Tsd/µl rather than G/L, which is " +
      "the same number under a different name. Its membership panel " +
      "resolves to the DE waitlist through isBookingOpen, and the quiz " +
      "hand-off in the result panel is gated on page:quiz, which DE does " +
      "not have. NL joined on the same terms and with the same kind of " +
      "vocabulary difference: Dutch laboratories name the enzymes ASAT and " +
      "ALAT, as Swiss ones do, but count platelets in ×10⁹/l rather than " +
      "G/L, which is again the same number under a different name. Two " +
      "things are the other way round there than in Germany: NL DOES have " +
      "the quiz (page:quiz is CH + NL), so the hand-off renders; and it has " +
      "no membership at all (page:membership, AHD-9361), so the closing " +
      "panel reads that gate and points at the advanced blood test instead " +
      "of a waitlist. AT still out: trimmed to a 13-page waitlist site.",
  },
  "page:tools/smaf5-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "sMAF-5 liver fibrosis risk calculator SEO tool page. CH + DE + NL, " +
      "on the same terms as the FIB-4 one rather than the SCORE2 one: " +
      "sMAF-5's six coefficients were fitted once, on US NHANES data, and " +
      "are applied unchanged in every country (lib/smaf5.ts), so there was " +
      "nothing in the model either later market needed wired up and the copy " +
      "was the whole of each rollout. LOCALES holds de/en/fr/nl, so CH " +
      "prerenders de/en/fr, DE prerenders `de`, its only language, and NL " +
      "prerenders nl + en. Same market vocabulary as the FIB-4 page in each " +
      "market: Germany reads AST rather than the Swiss ASAT and counts " +
      "platelets in Tsd/µl rather than G/L, while the Netherlands keeps ASAT " +
      "and writes platelets in ×10⁹/l. All of those are the same number " +
      "under a different name. Its membership panel resolves to the DE " +
      "waitlist through isBookingOpen; NL has no membership to resolve to at " +
      "all (page:membership, AHD-9361), so there the panel reads that gate " +
      "and points at the advanced blood test. The quiz hand-off in the " +
      "result panel is gated on page:quiz, which DE does not have and NL " +
      "does. AT still out: trimmed to a 13-page waitlist site.",
  },
  "page:tools/mafld-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "MAFLD criteria calculator SEO tool page. CH + DE + NL, on the same " +
      "terms as the FIB-4 and sMAF-5 ones rather than the SCORE2 one: the " +
      "MAFLD definition has no country term at all (lib/mafld.ts), so there " +
      "was nothing in the model either later market needed wired up. Its " +
      "one population caveat is ethnicity rather than market (the consensus " +
      "publishes lower BMI and waist cut-offs for Asian populations, and " +
      "this module implements the Caucasian set), which is the same caveat " +
      "in every country and is stated on the page. LOCALES holds " +
      "de/en/fr/nl, so CH prerenders de/en/fr, DE prerenders `de`, its only " +
      "language, and NL prerenders nl + en. Germany needed more than a " +
      "translation, unlike the other two liver pages, because this one " +
      "reads a lipid value and German labs report lipids in mg/dL: the " +
      "triglyceride field asks German readers in mg/dL and offers the " +
      "switch back to mmol/L (feature:tools-mgdl-lipids, " +
      "lib/lipid-units.ts). The Netherlands did NOT need that half: Dutch " +
      "laboratories report lipids in mmol/L already, the scale the model " +
      "computes in, so the field stays mmol/L there with no switch to " +
      "mis-set. What it needed instead was the closing panel, which reads " +
      "page:membership: NL sells no membership at all (AHD-9361), so there " +
      "the panel points at the advanced blood test. AT still out: trimmed " +
      "to a 13-page waitlist site.",
  },

  // --- Sections: block inside a shared page ---
  "section:insurance": {
    countries: ["ch", "de"],
    description:
      "Whole insurance/reimbursement section (heading + copy + coverage CTA), reused across homepage, pricing, etc. Hidden on NL — no reimbursement positioning there (AHD-6780).",
    // The `insurance` namespace is exclusively this section + the CH-only
    // /insurance page; nowhere un-gated renders it, so reviewers outside ch/de
    // can never see it.
    reviewKeyPrefixes: ["insurance."],
  },
  "section:home.hero-membership-pill": {
    countries: ["ch"],
    description:
      "'New: Ahead membership' pill above the homepage hero. CH-only — hidden on DE, and on NL while it launches as a waitlist (AHD-6898).",
  },
  "section:home.membership-banner": {
    countries: ["ch"],
    description:
      "Homepage 'Ahead membership' banner (HomepageMembershipSection — pill, heading, 'become a member' CTA, feature grid). CH-only — hidden on DE/AT (coming-soon EUR markets, membership not offered) and on NL, which launches blood-first without membership (AHD-6903).",
  },
  "section:home.swiss-insurer-logos": {
    countries: ["ch"],
    description: "Swiss insurer logo row in the homepage insurance section.",
  },
  "section:home.insurance-coverage-cta": {
    countries: ["ch"],
    description:
      "Insurance-section primary CTA points to /insurance (CH-only). Non-CH renders BookingCta instead.",
  },
  "section:marketing.insurance-coverage-cta": {
    countries: ["ch"],
    description:
      "Standalone insurance-coverage CTA block used on landing pages.",
  },
  "section:membership.locations-strip": {
    countries: ["ch"],
    description:
      "Swiss locations map on membership page — hidden on NL (AHD-6881).",
  },
  "section:contact.phone": {
    countries: ["ch"],
    description:
      "Prominent phone number on /contact (hero link + 'Call us' intent card, with a live-now dot during business hours). CH-only — it's a real staffed Swiss number (+41 44 797 69 46). NL has no staffed line, and DE/AT still ship placeholder contact data (AHD-8248). Originally removed site-wide by AHD-6829 (a NL-waitlist ticket that over-reached to CH); reinstated CH-scoped in AHD-10554.",
  },
  "section:marketing.quiz-cta-banner": {
    countries: ["ch", "nl"],
    description:
      "The quiz CTA banner (headline + CTA + phone mockups) on pricing, test detail, and /go landing pages, plus the CMS landing-page block. CH + NL — opened to NL so the funnel has an in-site entry point instead of being direct-URL-only. ⚠️ Deliberate known trade-off (Willem, 2026-07-29): the banner's headline/CTA ARE translated (ui.quizCta*), but its phone mockups are hardcoded English and still depict the retired 3-step external quiz, so NL visitors see stale English artwork next to Dutch copy until AHD-9270 redraws them. Opened knowingly to get NL traffic into the funnel sooner; if that reads as too rough, revert this to [\"ch\"] — it's a one-line change. The exit-intent quiz card is covered separately by feature:exit-intent (still CH-only). Earlier revisions of this note also claimed the gate covers 'navbar mega-menu and mobile-menu quiz banners' — there is no quiz reference anywhere in components/ui/navbar/, so that was stale.",
    // No reviewKeyPrefixes. This used to claim `quiz.` was CH-exclusive, which
    // stopped being true when page:quiz opened to NL — keeping it would drop
    // every Dutch quiz string from the NL reviewer's queue, which is the exact
    // opposite of what a newly-translated namespace needs.
  },

  // --- Features: behaviour gates that aren't a visible section ---
  "feature:membership-first-positioning": {
    countries: ["ch"],
    description:
      "Membership-first positioning across the homepage (hero + USP chips + value props + services + how-it-works + pricing + closing CTA), the navbar (mega-menu membership card, no standalone Membership item, 'become a member' CTA), /pricing (membership tab leads), test detail hero prices (member rate leads), and the /how-it-works membership start line. Shipped from the homepage-membership-first-v1 experiment (AHD-6700). CH only — every other market keeps the testing-first copy: NL launches blood-first with no membership product (AHD-6910), and DE/AT are pre-launch EUR waitlist markets whose member pricing is CHF-only, so they advertise membership as coming-soon through page:membership instead of leading with it. AT was dropped from this gate (2026-08-28) because it had membership-led hero/nav/pricing copy with no AT membership product or EUR member price behind it — the same contradiction DE was always kept out of. Wider than a single section, so it's a feature gate; the mf* i18n keys are shared with the un-gated markets' fallbacks, so no reviewKeyPrefixes.",
  },
  "feature:exit-intent": {
    countries: ["ch"],
    description:
      "Exit-intent provider mounts only in CH (NL/DE don't have the funnel signal yet).",
  },
  "feature:nav-login": {
    countries: ["ch"],
    description:
      "Login link in navbar (desktop + mobile) — hidden in DE/NL until my.aheadhealth.com supports those markets.",
  },
  "feature:nav-search": {
    countries: ["ch"],
    description:
      "Desktop search icon in the navbar — CH only. Shipped to the base nav from the navbar-v4 test's search-icon win (v3 had dropped it globally); kept CH-scoped to match what was validated.",
  },
  "feature:chat": {
    countries: ["ch", "nl"],
    description:
      "The Ella chat — the global launcher (in the [locale] layout) + every " +
      "page-scoped chat surface (tests/biomarkers/what-we-detect/symptoms " +
      "detail `chatEligible`, and the AiAnswerLane mounts on fullbodymri / " +
      "insurance / search). An allow-list, not 'on wherever launched': chat " +
      "may only appear in a market the customer-support stack can actually " +
      "ground and staff. CH first; NL added at the 2026-09 Dutch go-live " +
      "(AHD-12443) — bilingual on both /nl/nl and /nl/en (the launcher keys " +
      "on country, so one entry covers both locales), after the cs side was " +
      "confirmed live: nl/nl CMS fallback suppression, the draft-only " +
      "audience gate (LP_KB_AUDIENCE_GATE_ENABLED) so no agent-only policy " +
      "reaches chat, a bilingual NL KB, and a dedicated NL Chatwoot handoff " +
      "team (CHATWOOT_NL_TEAM_ID). Adding `nl` here both mounts the real " +
      "launcher for everyone and retires the `ella-nl` preview path (the " +
      "EllaNlPreviewLauncher self-gates off once this includes nl). DE/AT " +
      "stay off until their own go-live clears the same bar. AiAnswerLane " +
      "surfaces still layer their own `aiAssistantEnabled()` env gate on top " +
      "via the `envEnabled` prop.",
  },
  "feature:tools-mgdl-lipids": {
    countries: ["de"],
    description:
      "Lipid entry in mg/dL on the calculator pages, rather than the mmol/L " +
      "the models compute in: the triglyceride field on " +
      "/tools/mafld-calculator and the two cholesterol fields on " +
      "/tools/score2-calculator. DE only, because this is a laboratory " +
      "convention rather than a preference — German labs report lipids in " +
      "mg/dL where Swiss ones report mmol/L, so in DE the mmol/L-only field " +
      "refused the correct number from the reader's own report and asked " +
      "them to convert, which is exactly the arithmetic that produces a " +
      "confidently wrong answer (159 typed into a term expecting 1.8 prints " +
      '"fat likely" over a clean liver). Where the gate allows a market, ' +
      "the field DEFAULTS to mg/dL and offers a switch to mmol/L; elsewhere " +
      "it is mmol/L with no switch, byte-identical to before. Both answers " +
      "come from lib/lipid-units.ts, which reads this gate, so adding a " +
      "market is this country list and nothing else. No reviewKeyPrefixes: " +
      "the unit copy lives in each calculator's own namespace beside " +
      "un-gated strings.",
  },
} as const satisfies Record<string, CountryGate>

export type GateId = keyof typeof COUNTRY_GATES

/** GateIds that refer to whole pages (route-level gates). */
export type PageGateId = Extract<GateId, `page:${string}`>

/** Is this gate's content allowed in the given country? */
export function isAllowedInCountry(id: GateId, country: Country): boolean {
  return (COUNTRY_GATES[id].countries as readonly string[]).includes(country)
}

/**
 * True when `key`'s copy belongs to a gate whose `reviewKeyPrefixes` claim it
 * and that gate is NOT allowed in `country` — i.e. a reviewer there can never
 * see the string in context. Used by the translation-review queue to drop
 * section/feature-gated copy; page gates are filtered separately by render path.
 */
export function isKeyCountryGatedOut(key: string, country: string): boolean {
  for (const gate of Object.values(COUNTRY_GATES) as CountryGate[]) {
    const prefixes = gate.reviewKeyPrefixes
    if (!prefixes) continue
    if (
      prefixes.some((p) => key.startsWith(p)) &&
      !(gate.countries as readonly string[]).includes(country)
    ) {
      return true
    }
  }
  return false
}

/**
 * Per-document market visibility — the data-driven companion to the static
 * registry above. CMS content collections (faq, blog-posts) carry an editor-set
 * `hideInCountries` deny-list; this returns true when the current market is on
 * it. Empty / absent list = visible everywhere. Where the registry gates whole
 * pages/sections, this gates individual CMS rows.
 */
export function isHiddenInCountry(
  doc: { hideInCountries?: readonly string[] | null },
  country: string
): boolean {
  return (
    Array.isArray(doc.hideInCountries) && doc.hideInCountries.includes(country)
  )
}

/**
 * Country list for a `page:` gate. Use in `generateStaticParams`:
 *
 *   export function generateStaticParams() {
 *     return countriesForGatedPage("page:insurance").flatMap((country) =>
 *       getCountryConfigStatic(country).languages.map((locale) => ({
 *         country,
 *         locale,
 *       }))
 *     )
 *   }
 */
export function countriesForGatedPage(id: PageGateId): readonly Country[] {
  return COUNTRY_GATES[id].countries
}

/**
 * Map from a `META` page key (e.g. "insurance") to its `page:` gate ID.
 * Used by the search-index builder to translate page-keyed iteration
 * into gate lookups. Other surfaces (sitemap, hreflang) declare their
 * gate inline at each call site and don't need this map.
 *
 * Every `page:` entry in COUNTRY_GATES must appear as a value here. The
 * test in __tests__/country-gates.test.ts enforces parity.
 */
export const PAGE_KEY_TO_GATE_ID = {
  insurance: "page:insurance",
  membership: "page:membership",
  pricing: "page:pricing",
  careers: "page:careers",
  authors: "page:authors",
  ambassadors: "page:ambassadors",
  ambassadorsApplied: "page:ambassadors",
  "insurance-checker-terms": "page:insurance-checker-terms",
  "full-body-mri-zurich": "page:full-body-mri-zurich",
  "go/bloodtest-munich": "page:go/bloodtest-munich",
  "go/bloodtest-berlin": "page:go/bloodtest-berlin",
  "go/bloodtest-frankfurt": "page:go/bloodtest-frankfurt",
  "go/bloodtest-hamburg": "page:go/bloodtest-hamburg",
  "go/bloodtest-zurich": "page:go/bloodtest-zurich",
  gift: "page:gift",
  quiz: "page:quiz",
  preventionSpecialistGpLanding:
    "page:careers/prevention-specialist-gp-landing",
  freeCall: "page:free-call",
  insights: "page:insights",
  compareHealthCheckUps: "page:compare-health-check-ups-switzerland",
  cortisolBloodTest: "page:tests/cortisol-blood-test",
  psaBloodTest: "page:tests/psa-blood-test",
  faq: "page:faq",
  biomarkers: "page:biomarkers",
  lpaBloodTest: "page:tests/lpa",
  symptoms: "page:symptoms",
  teams: "page:teams",
  // Not META keys - the index and each calculator carry their own
  // title/description in their own namespace and none is in the search index,
  // so nothing iterating META reaches these entries. They exist because the
  // PAGE_KEY_TO_GATE_ID parity test requires every `page:` gate to be wired
  // here (same as "full-body-mri-zurich").
  tools: "page:tools",
  bmiCalculator: "page:tools/bmi-calculator",
  bmrCalculator: "page:tools/bmr-calculator",
  score2Calculator: "page:tools/score2-calculator",
  fib4Calculator: "page:tools/fib4-calculator",
  smaf5Calculator: "page:tools/smaf5-calculator",
  mafldCalculator: "page:tools/mafld-calculator",
} as const satisfies Record<string, PageGateId>

/**
 * Look up the page-gate (if any) for a given META page key. Returns
 * undefined for keys that aren't country-gated. Use this from any code
 * iterating META keys (e.g. the search-index builder); call sites with
 * a literal pageKey can index PAGE_KEY_TO_GATE_ID directly.
 */
export function getPageGateForKey(key: string): PageGateId | undefined {
  return (PAGE_KEY_TO_GATE_ID as Record<string, PageGateId | undefined>)[key]
}

/**
 * Map from URL path (without `/{country}/{locale}` prefix, leading slash
 * required) to the `page:` gate that governs it. Used by the locale
 * picker to decide whether to fall back to the country home when the
 * visitor switches to a country where the current page doesn't exist.
 */
export const PATH_TO_PAGE_GATE = {
  "/insurance": "page:insurance",
  "/membership": "page:membership",
  "/pricing": "page:pricing",
  "/faq": "page:faq",
  "/biomarkers": "page:biomarkers",
  "/tests/lpa": "page:tests/lpa",
  "/symptoms": "page:symptoms",
  "/teams": "page:teams",
  "/careers": "page:careers",
  "/authors": "page:authors",
  "/ambassadors": "page:ambassadors",
  "/ambassadors/applied": "page:ambassadors",
  "/insurance-checker-terms": "page:insurance-checker-terms",
  "/full-body-mri-zurich": "page:full-body-mri-zurich",
  "/go/bloodtest-munich": "page:go/bloodtest-munich",
  "/go/bloodtest-berlin": "page:go/bloodtest-berlin",
  "/go/bloodtest-frankfurt": "page:go/bloodtest-frankfurt",
  "/go/bloodtest-hamburg": "page:go/bloodtest-hamburg",
  "/go/bloodtest-zurich": "page:go/bloodtest-zurich",
  "/gift": "page:gift",
  "/quiz": "page:quiz",
  "/careers/prevention-specialist-gp-landing":
    "page:careers/prevention-specialist-gp-landing",
  "/free-call": "page:free-call",
  "/insights": "page:insights",
  "/compare-health-check-ups-switzerland":
    "page:compare-health-check-ups-switzerland",
  "/tests/cortisol-blood-test": "page:tests/cortisol-blood-test",
  "/tests/psa-blood-test": "page:tests/psa-blood-test",
  "/tools": "page:tools",
  "/tools/bmi-calculator": "page:tools/bmi-calculator",
  "/tools/bmr-calculator": "page:tools/bmr-calculator",
  "/tools/score2-calculator": "page:tools/score2-calculator",
  "/tools/fib4-calculator": "page:tools/fib4-calculator",
  "/tools/smaf5-calculator": "page:tools/smaf5-calculator",
  "/tools/mafld-calculator": "page:tools/mafld-calculator",
} as const satisfies Record<string, PageGateId>

/**
 * Resolve which `page:` gate (if any) applies to a bare path. Returns
 * undefined for paths that aren't country-gated.
 */
export function gateForPath(path: string): PageGateId | undefined {
  // Normalise a translated URL (/versicherung) back to its English route
  // (/insurance) so a gate is keyed once, not once per locale.
  const canonical = canonicalSlugPath(path)
  return (PATH_TO_PAGE_GATE as Record<string, PageGateId | undefined>)[
    canonical
  ]
}

/**
 * Pages that render but are deliberately kept OUT of the index, by country.
 * `"all"` means every market.
 *
 * This is separate from a `page:` gate: a gated page doesn't exist in the
 * country at all, whereas these are live and linkable — they just must not be
 * submitted for indexing.
 *
 * The rule lives here because two surfaces need the same answer and used to
 * disagree: the page sets `robots: { index: false }`, and the sitemap has to
 * not list it. It listed all 7 anyway (AHD-10527). A noindexed URL in a
 * sitemap is a direct contradiction — we're asking Google to index a page that
 * tells it not to.
 */
export const NOINDEX_IN_COUNTRIES = {
  // Site search: a search-results page never belongs in an index, anywhere.
  "/search": "all",
  // DE FAQ is unreachable from the German nav and has no Germany-specific
  // content yet; kept live so internal deep-links don't 404.
  "/faq": ["de"],
} as const satisfies Record<string, "all" | readonly Country[]>

/**
 * Is a bare path noindexed in this country? Leading slash optional.
 */
export function isNoindexedInCountry(path: string, country: Country): boolean {
  const norm =
    path === "" || path === "/" ? "/" : `/${path.replace(/^\/+/, "")}`
  const rule = (
    NOINDEX_IN_COUNTRIES as Record<
      string,
      "all" | readonly Country[] | undefined
    >
  )[norm]
  if (!rule) return false
  return rule === "all" || rule.includes(country)
}

/**
 * Is a bare render path (locale-stripped, with or without a leading slash;
 * "" or "/" = home) reachable in a country? Ungated paths exist in every
 * country; gated paths follow their `page:` gate's country list. Used both to
 * drop unreachable strings from a reviewer's queue and to steer the cockpit's
 * jump to a page that actually exists in the locale under review.
 */
export function isPathAllowedInCountry(
  bare: string,
  country: Country
): boolean {
  const norm =
    bare === "" || bare === "/" ? "/" : `/${bare.replace(/^\/+/, "")}`
  const gate = gateForPath(norm)
  return gate ? isAllowedInCountry(gate, country) : true
}
